// ==================================
// Pagina Nuevo Administrador
// ==================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import { Button, Input, Card, CardHeader } from '@/componentes/ui';
import { superadminsServicio } from '@/servicios';
import type { PermisosSuperAdmin } from '@/tipos';

export function NuevoAdministrador() {
  const navigate = useNavigate();

  const [nombre, setNombre] = useState('');
  const [apellidoPaterno, setApellidoPaterno] = useState('');
  const [apellidoMaterno, setApellidoMaterno] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telefono, setTelefono] = useState('');
  const [permisos, setPermisos] = useState<PermisosSuperAdmin>({
    crearVeterinarias: true,
    eliminarVeterinarias: false,
    modificarLicencias: false,
    verReportesGlobales: true,
    gestionarSuperAdmins: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const crearMutation = useMutation({
    mutationFn: () =>
      superadminsServicio.crear({
        nombre,
        apellidoPaterno,
        apellidoMaterno: apellidoMaterno || undefined,
        email,
        password,
        telefono: telefono || undefined,
        permisosSuperAdmin: permisos,
      }),
    onSuccess: () => {
      toast.success('Administrador creado exitosamente');
      navigate('/administradores');
    },
    onError: () => {
      toast.error('Error al crear el administrador');
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!apellidoPaterno.trim()) {
      newErrors.apellidoPaterno = 'El apellido paterno es requerido';
    }

    if (!email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email inválido';
    }

    if (!password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      crearMutation.mutate();
    }
  };

  return (
    <div>
      <Header titulo="Nuevo Administrador" />

      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informacion del Administrador */}
            <Card>
              <CardHeader
                title="Información del Administrador"
                subtitle="Datos básicos del super administrador"
              />
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Nombre"
                    value={nombre}
                    onChange={(e) => {
                      setNombre(e.target.value);
                      if (errors.nombre) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.nombre;
                          return newErrors;
                        });
                      }
                    }}
                    error={errors.nombre}
                    placeholder="Juan"
                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                  />
                  <Input
                    label="Apellido Paterno"
                    value={apellidoPaterno}
                    onChange={(e) => {
                      setApellidoPaterno(e.target.value);
                      if (errors.apellidoPaterno) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.apellidoPaterno;
                          return newErrors;
                        });
                      }
                    }}
                    error={errors.apellidoPaterno}
                    placeholder="Pérez"
                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                  />
                </div>
                <Input
                  label="Apellido Materno (opcional)"
                  value={apellidoMaterno}
                  onChange={(e) => setApellidoMaterno(e.target.value)}
                  placeholder="García"
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                />
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.email;
                        return newErrors;
                      });
                    }
                  }}
                  error={errors.email}
                  placeholder="admin@alebrijecloud.com"
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                />
                <Input
                  label="Contraseña"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.password;
                        return newErrors;
                      });
                    }
                  }}
                  error={errors.password}
                  placeholder="Mínimo 8 caracteres"
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
                />
                <Input
                  label="Teléfono (opcional)"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="55 1234 5678"
                  icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                />
              </div>
            </Card>

            {/* Permisos */}
            <Card>
              <CardHeader
                title="Permisos"
                subtitle="Permisos del super administrador"
              />
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permisos.crearVeterinarias}
                    onChange={(e) => setPermisos({ ...permisos, crearVeterinarias: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-obsidian-900">Crear veterinarias</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permisos.eliminarVeterinarias}
                    onChange={(e) => setPermisos({ ...permisos, eliminarVeterinarias: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-obsidian-900">Eliminar veterinarias</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permisos.modificarLicencias}
                    onChange={(e) => setPermisos({ ...permisos, modificarLicencias: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-obsidian-900">Modificar licencias</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permisos.verReportesGlobales}
                    onChange={(e) => setPermisos({ ...permisos, verReportesGlobales: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-obsidian-900">Ver reportes globales</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permisos.gestionarSuperAdmins}
                    onChange={(e) => setPermisos({ ...permisos, gestionarSuperAdmins: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-obsidian-900">Gestionar super admins</span>
                </label>
              </div>
            </Card>
          </div>

          {/* Acciones */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/administradores')}
              leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={crearMutation.isPending}
              leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Crear Administrador
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NuevoAdministrador;
